import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { API } from '@/api'
import { GetTeamInfoRes } from '@/api/type-response'
import { Button, Trans } from '@/components'
import { Input } from '@/components/base/Input'
import { Loading } from '@/components/base/Loading'
import { Modal } from '@/components/base/Modal'
import { Textarea } from '@/components/base/Textarea'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatDatePreset, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

interface TeamInfoItemProps {
  label: string | React.ReactNode
  value: string
  fullWidth?: boolean
}

function TeamInfoItem({ label, value, fullWidth }: TeamInfoItemProps) {
  return (
    <div
      className={cn(
        'box-border flex flex-col gap-1 px-6 py-4 rounded-lg',
        fullWidth ? 'w-full' : 'basis-0 grow min-w-[512px]',
      )}
    >
      <p className="t-sb text-tertiary w-full">{label}</p>
      <div className="flex gap-1 items-center w-full">
        <p className={cn('t-sb text-primary', fullWidth ? 'flex-1' : 'whitespace-pre text-nowrap')}>
          {value}
        </p>
      </div>
    </div>
  )
}

export function TeamSettingPage() {
  const [teamInfo, setTeamInfo] = useState<GetTeamInfoRes['team'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [memberCount, setMemberCount] = useState<number>(0)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({ name: '', intro: '' })
  const [saving, setSaving] = useState(false)
  const [showManagePlanModal, setShowManagePlanModal] = useState(false)
  const teamStore = useTeamStore()
  const queryClient = useQueryClient()
  const userStore = useUserStore()

  useEffect(() => {
    const fetchTeamInfo = async () => {
      try {
        const teamResID = teamStore.teamTokenData?.teamResID
        if (!teamResID) {
          console.error('No team res ID found')
          return
        }

        const teamInfoRes = await API.getTeamInfo({ res_id: teamResID })

        setTeamInfo(teamInfoRes.team)
        setMemberCount(teamInfoRes.team.memberStat.membersCount)
      } catch (error) {
        console.error('Failed to fetch team info:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchTeamInfo()
  }, [teamStore.teamTokenData?.teamResID])

  const handleEdit = () => {
    if (!teamInfo) return
    setEditForm({
      name: teamInfo.team.name,
      intro: teamInfo.team.introduction,
    })
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (!teamInfo) return

    setSaving(true)
    try {
      await API.updateTeamInfo({
        res_id: teamInfo.team.desc.resID,
        name: editForm.name,
        intro: editForm.intro,
      })

      const teamInfoRes = await API.getTeamInfo({ res_id: teamInfo.team.desc.resID })

      setTeamInfo(teamInfoRes.team)
      setMemberCount(teamInfoRes.team.memberStat.membersCount)
      setIsEditing(false)

      // Invalidate team list cache to update TeamSwitcher
      queryClient.invalidateQueries({ queryKey: ['teamList'] })
    } catch (error) {
      console.error('Failed to update team info:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  const handleSwitchToPlatform = () => {
    setShowManagePlanModal(false)
    teamStore.clearTeamContext()
    window.location.href = '/create'
  }

  if (loading) {
    return (
      <div className="card bg-panel-white h-full flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!teamInfo) {
    return (
      <div className="card bg-panel-white h-full flex items-center justify-center">
        <div>Failed to load team information</div>
      </div>
    )
  }

  const { team } = teamInfo

  return (
    <div className="bg-panel-white box-border flex flex-col gap-8 overflow-clip p-8 rounded-[32px] h-full overflow-y-auto">
      {/* Team Info Section */}
      <div className="bg-panel-white border-b border-neutral-200 border-solid box-border flex gap-4 h-[60px] pb-4">
        <div className="flex-1 flex flex-col">
          <div className="flex gap-6 h-7">
            <div className="flex gap-2 items-center justify-center">
              <p className="h6 text-primary whitespace-pre text-nowrap">
                <Trans i18nKey="team.setting.teamInfo">Team Info</Trans>
              </p>
            </div>
          </div>
        </div>
        {isEditing ? (
          <div className="flex gap-2">
            <Button type="outline" onClick={handleCancel} className="min-w-[88px]">
              <Trans i18nKey="team.setting.cancel">Cancel</Trans>
            </Button>
            <Button
              type="secondary"
              onClick={handleSave}
              disabled={saving}
              className="min-w-[88px]"
            >
              {saving ? (
                <Trans i18nKey="team.setting.saving">Saving...</Trans>
              ) : (
                <Trans i18nKey="team.setting.save">Save</Trans>
              )}
            </Button>
          </div>
        ) : (
          <div className="flex gap-4">
            <Button
              type="default"
              onClick={() => setShowManagePlanModal(true)}
              className="min-w-[88px]"
            >
              <Trans i18nKey="team.setting.managePlan">Manage Plan</Trans>
            </Button>
            <Button type="default" onClick={handleEdit} className="min-w-[88px]">
              <Trans i18nKey="team.setting.edit">Edit</Trans>
            </Button>
          </div>
        )}
      </div>

      {/* Team Info Parameters */}
      <div className="flex flex-wrap gap-2 w-full">
        <TeamInfoItem
          label={<Trans i18nKey="team.setting.teamID">Team ID</Trans>}
          value={team.desc.resID}
        />
        {isEditing ? (
          <div
            className={cn(
              'box-border flex flex-col gap-1 px-6 py-4 rounded-lg',
              'basis-0 grow min-w-[512px]',
            )}
          >
            <p className="t-sb text-tertiary w-full">
              <Trans i18nKey="team.setting.teamName">Team name</Trans>
            </p>
            <Input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              placeholder="Enter team name"
              className="t-sb"
            />
          </div>
        ) : (
          <TeamInfoItem
            label={<Trans i18nKey="team.setting.teamName">Team name</Trans>}
            value={team.name}
          />
        )}
        {isEditing ? (
          <div className={cn('box-border flex flex-col gap-1 px-6 py-4 rounded-lg', 'w-full')}>
            <p className="t-sb text-tertiary w-full">
              <Trans i18nKey="team.setting.teamDescription">Team description</Trans>
            </p>
            <Textarea
              value={editForm.intro}
              onChange={(e) => setEditForm({ ...editForm, intro: e.target.value })}
              placeholder="Enter team description"
              className="t-sb overflow-y-auto"
            />
          </div>
        ) : (
          <TeamInfoItem
            label={<Trans i18nKey="team.setting.teamDescription">Team description</Trans>}
            value={team.introduction}
            fullWidth
          />
        )}
        <TeamInfoItem
          label={<Trans i18nKey="team.setting.teamBalance">Team balance</Trans>}
          value={`${formatPrice(
            userStore.account.totalBonus.moneyCent ?? 0,
            false,
            userStore.account.totalBonus.moneyCent > 100 ? 'Credits' : 'Credit',
          )}`}
        />
        <TeamInfoItem
          label={<Trans i18nKey="team.setting.teamExpirationTime">Team expiration time</Trans>}
          value={
            teamInfo.order?.deadlineTime
              ? formatDatePreset(teamInfo.order.deadlineTime.seconds * 1000, 'day')
              : '-'
          }
        />
        <TeamInfoItem
          label={<Trans i18nKey="team.setting.teamSize">Team size</Trans>}
          value={`${memberCount}/${team.profile.limit.membersNumLimit}`}
        />
        {/* Keep the final row aligned */}
        <div className="basis-0 grow min-w-[512px] opacity-0 px-6 py-4 rounded-lg">
          <p className="t-sb text-tertiary" aria-hidden="true">
            &nbsp;
          </p>
        </div>
      </div>

      <Modal open={showManagePlanModal} onClose={() => setShowManagePlanModal(false)}>
        <Modal.Header>
          <Trans i18nKey="team.setting.managePlan">Manage Plan</Trans>
        </Modal.Header>
        <Modal.Body>
          <Trans i18nKey="team.setting.managePlanDescription">
            To adjust your plan, please navigate to <span className="text-highlight">Glows.ai</span>{' '}
            &gt; Account &gt; Team.
          </Trans>
        </Modal.Body>
        <div className="pt-2">
          <Button type="secondary" onClick={handleSwitchToPlatform} className="w-full">
            <Trans i18nKey="team.setting.goToGlows">Go to Glows.ai</Trans>
          </Button>
        </div>
      </Modal>
    </div>
  )
}
